<template>
  <div class="base-carousel">
    <Carousel
      ref="carouselRef"
      :items-to-show="1"
      :wrap-around="true"
      :transition="500"
      :mouse-drag="!props.disabled"
      :touch-drag="!props.disabled"
      :model-value="currentSlide"
      class="base-carousel__carousel"
      @slide-end="handleSlideEnd"
    >
      <Slide
        v-for="(item, index) in props.items"
        :key="index"
        class="base-carousel__slide"
      >
        <slot
          name="slide"
          :item="item"
          :index="index"
        >
          <div class="base-carousel__slide-content">
            {{ item }}
          </div>
        </slot>
      </Slide>

      <template #addons>
        <div v-if="!props.disabled" class="base-carousel__navigation">
          <BaseButtonIcon
            icon-name="vuesax:arrow-left"
            icon-color="var(--color-foreground-inverted)"
            form="circle"
            variant="transparent-inverted"
            size="md"
            class="base-carousel__navigation-button base-carousel__navigation-button--prev"
            @click="handlePrev"
          />
          <BaseButtonIcon
            icon-name="vuesax:arrow-right"
            icon-color="var(--color-foreground-inverted)"
            form="circle"
            variant="transparent-inverted"
            size="md"
            class="base-carousel__navigation-button base-carousel__navigation-button--next"
            @click="handleNext"
          />
        </div>
        <div v-if="!props.disabled && props.withDots" class="base-carousel__pagination">
          <div
            v-if="useInstagramPagination"
            class="base-carousel__pagination-inner"
          >
            <div
              class="base-carousel__pagination-strip"
              :style="{ transform: `translateX(-${paginationTranslateOffset}px)` }"
            >
              <span
                v-for="(_, index) in props.items"
                :key="index"
                class="base-carousel__pagination-slot"
                :class="getPaginationSlotClass(index)"
                @click="handleSlideTo(index)"
              >
                <span
                  class="base-carousel__pagination-dot"
                  :class="getPaginationDotClass(index)"
                />
              </span>
            </div>
          </div>
          <template v-else>
            <span
              v-for="(_, index) in props.items"
              :key="index"
              class="base-carousel__pagination-dot"
              :class="getPaginationDotClass(index)"
              @click="handleSlideTo(index)"
            />
          </template>
        </div>
      </template>
    </Carousel>
  </div>
</template>

<script setup lang="ts">
import { Carousel, Slide } from 'vue3-carousel'
import { BaseButtonIcon } from '~/shared/ui'
import 'vue3-carousel/dist/carousel.css'

interface Props {
  items: any[]
  disabled?: boolean
  selectedIndex?: number
  withDots?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  selectedIndex: 0,
  withDots: true,
})

const emit = defineEmits<{
  (e: 'update:selectedIndex', index: number): void
}>()

const carouselRef = ref<InstanceType<typeof Carousel> | null>(null)
const currentSlide = ref(props.selectedIndex)

const SLOT_WIDTH_ACTIVE = 28
const SLOT_WIDTH_MEDIUM = 10
const SLOT_WIDTH_SMALL = 8
const SLOT_GAP = 4
const VISIBLE_SLOTS_COUNT = 7

const useInstagramPagination = computed(() => props.items.length > VISIBLE_SLOTS_COUNT)

const paginationStartIndex = computed(() => {
  const total = props.items.length
  if (total <= VISIBLE_SLOTS_COUNT) {
    return 0
  }

  return Math.max(0, Math.min(currentSlide.value - 2, total - VISIBLE_SLOTS_COUNT))
})

function getSlotWidth(index: number) {
  const startIndex = paginationStartIndex.value
  const isLastVisible = index === startIndex + VISIBLE_SLOTS_COUNT - 1
  const isActive = index === currentSlide.value

  return isActive ? SLOT_WIDTH_ACTIVE : isLastVisible ? SLOT_WIDTH_SMALL : SLOT_WIDTH_MEDIUM
}

const paginationTranslateOffset = computed(() => {
  let offset = 0
  for (let i = 0; i < paginationStartIndex.value; i++) {
    offset += getSlotWidth(i) + SLOT_GAP
  }
  return offset
})

function getPaginationSlotClass(index: number) {
  const startIndex = paginationStartIndex.value
  const isLastVisible = index === Math.min(startIndex + VISIBLE_SLOTS_COUNT - 1, props.items.length - 1)
  const isActive = index === currentSlide.value

  return {
    'base-carousel__pagination-slot--size-small': isLastVisible && !isActive,
    'base-carousel__pagination-slot--size-medium': !isLastVisible && !isActive,
    'base-carousel__pagination-slot--size-active': isActive,
  }
}

function getPaginationDotClass(index: number) {
  const isLastVisible = index === Math.min(paginationStartIndex.value + VISIBLE_SLOTS_COUNT - 1, props.items.length - 1)
  const isActive = index === currentSlide.value

  return {
    'base-carousel__pagination-dot--active': isActive,
    'base-carousel__pagination-dot--size-small': isLastVisible && !isActive,
  }
}

watch(() => props.selectedIndex, (newVal) => {
  currentSlide.value = newVal
})

function handlePrev() {
  const carousel = carouselRef.value as any

  carousel?.prev?.()
}

function handleNext() {
  const carousel = carouselRef.value as any

  carousel?.next?.()
}

function handleSlideTo(index: number) {
  const carousel = carouselRef.value as any

  carousel?.slideTo?.(index)
}

function handleSlideEnd(data: { currentSlideIndex: number }) {
  currentSlide.value = data.currentSlideIndex
  emit('update:selectedIndex', data.currentSlideIndex)
}

function restartCarousel() {
  const carousel = carouselRef.value as {
    restartCarousel?: () => void
  } | null

  carousel?.restartCarousel?.()
}

defineExpose({
  restartCarousel,
})
</script>

<style lang="scss" scoped>
.base-carousel {
  position: relative;
  width: 100%;

  &__carousel {
    position: relative;
    width: 100%;
    height: 100%;

    :deep(.carousel__viewport) {
      overflow: hidden;
    }

    :deep(.carousel__track) {
      align-items: stretch;
    }
  }

  &__slide {
    width: 100%;

    &-content {
      width: 100%;
      height: 100%;
    }
  }

  &__navigation {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    transform: translateY(-50%);
    display: flex;
    justify-content: space-between;
    align-items: center;
    pointer-events: none;
    z-index: 2;
    padding: 0 rem(16px);
  }

  &__navigation-button {
    pointer-events: auto;
    background-color: rgb(0 0 0 / 30%);
    backdrop-filter: blur(4px);
    transition: background-color 0.3s ease;

    &:hover {
      background-color: rgb(0 0 0 / 50%);
    }

    &--prev {
      margin-right: auto;
    }

    &--next {
      margin-left: auto;
    }
  }

  &__pagination {
    background-color: rgb(0 0 0 / 30%);
    position: absolute;
    bottom: rem(20px);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: rem(4px);
    padding: rem(4px) rem(8px);
    border-radius: rem(40px);
    align-items: center;
    justify-content: center;
    z-index: 2;
    pointer-events: none;
  }

  &__pagination-inner {
    overflow: hidden;
    width: rem(110px); /* 7 слотов: 1×28px + 5×10px + 1×8px + 6×4px gap */
  }

  &__pagination-strip {
    display: flex;
    gap: rem(4px);
    align-items: center;
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }

  &__pagination-dot {
    width: rem(8px);
    height: rem(8px);
    border-radius: 50%;
    background-color: rgb(255 255 255 / 80%);
    cursor: pointer;
    pointer-events: auto;
    transition: all 0.3s ease;

    &--size-small {
      width: rem(4px);
      height: rem(4px);
    }

    &--active {
      background-color: rgb(255 255 255 / 100%);
    }

    &:hover {
      background-color: rgb(255 255 255 / 100%);
      transform: scale(1.2);
    }
  }

  &__pagination-slot {
    height: rem(12px);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    pointer-events: auto;
    flex-shrink: 0;

    &--size-medium {
      flex: 0 0 rem(10px);
      width: rem(10px);

      .base-carousel__pagination-dot {
        width: rem(6px);
        height: rem(6px);
      }
    }

    &--size-active {
      flex: 0 0 rem(28px);
      width: rem(28px);

      .base-carousel__pagination-dot {
        width: rem(24px);
        height: rem(8px);
        border-radius: rem(4px);
        background-color: rgb(255 255 255 / 100%);
      }
    }

    &--size-small {
      flex: 0 0 rem(8px);
      width: rem(8px);
    }

    &:hover .base-carousel__pagination-dot {
      background-color: rgb(255 255 255 / 100%);
      transform: scale(1.2);
    }
  }
}
</style>
